import { useCallback, useMemo, useState } from "react";

export const useSelectedObjects = <T extends { id: string }>(initial: T[]) => {
  const key = "id";
  const [values, setValues] = useState(initial);

  const toggle = useCallback(
    (value: any) => {
      const index = values.findIndex((v) => v[key] === value[key]);
      if (index === -1) {
        setValues([...values, value]);
      } else {
        setValues(values.filter((v) => v[key] !== value[key]));
      }
    },
    [values, key],
  );

  const contains = useCallback(
    (thing: T) => {
      return values.some((v) => v[key] === thing[key]);
    },
    [key, values],
  );

  const add = useCallback((...things: T[]) => {
    setValues((values) => [...values, ...things]);
  }, []);

  const remove = useCallback(
    (...things: T[]) => {
      const keys = things.map((thing) => thing[key]);
      setValues((values) => values.filter((v) => !keys.includes(v[key])));
    },
    [key],
  );

  const clear = useCallback(() => {
    setValues([]);
  }, []);

  const set = useCallback((things: T[]) => {
    setValues(things);
  }, []);

  return useMemo(
    () => ({
      add,
      clear,
      contains,
      remove,
      set,
      toggle,
      values,
    }),
    [add, clear, contains, remove, set, toggle, values],
  );
};
